import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';

const SPINNER_BUFFER_TIME = 800; // ms
const OPACITY_TRANSITION_TIME = 300; // ms, from components.loading.scss

export default class Loading extends React.Component {
  static propTypes = {
    variant: PropTypes.oneOf([
      'inline',
      'small',
      'sip',
      'button',
      'full-page',
    ]),
    children: PropTypes.node,
  };

  static defaultProps = {
    variant: null,
    children: null,
  };

  constructor(props, context) {
    super(props, context);

    this.render = this.render.bind(this);
    this.start = this.start.bind(this);
    this.stop = this.stop.bind(this);
    this.state = {
      complete: true,
      stopping: false,
    };
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentDidUpdate() {
    if (!this.loadingDiv) return;

    if (this.props.variant !== 'button') {
      this.loadingDiv.style.height = this.state.complete
        ? ''
        : `${this.loadingDiv.parentElement.getBoundingClientRect().height}px`;
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.completeTracking) {
      clearTimeout(this.completeTracking());
    }
  }

  get loadingPanelClassNames() {
    return this.state.complete ? 'loading-panel-loaded' : 'loading-panel-loading';
  }

  get spinnerPanelClassNames() {
    return [
      'spinner',
      this.state.stopping ? 'stopping' : '',
    ].join(' ');
  }

  get contentPanelClassNames() {
    return [
      'loading-panel-content',
      this.state.stopping ? 'loading-panel-stopping' : '',
    ].join(' ');
  }

  // options:
  // - onFadeInComplete: function, called when the fade in is complete
  start(options = {}) {
    if (!this.mounted) return;

    this.setState({ complete: false });

    const getTime = () => new Date().getTime();
    const startTime = getTime();

    // NOTE Create a (private) method called by stop() that waits for at least
    // `SPINNER_BUFFER_TIME` ms and then destroys itself and sets the state to complete.
    // This is to add a buffer so that the spinner is visible for at least 800
    // ms, otherwise it looks like the app didn't do anything.
    // https://www.nngroup.com/articles/response-times-3-important-limits/
    this.completeTracking = () => setTimeout(
      () => {
        this.completeTracking = null;
        if (this.mounted) {
          this.setState({ complete: true });
        }
      },
      Math.max(SPINNER_BUFFER_TIME + startTime - getTime(), 0),
    );

    if (options.onFadeInComplete) {
      setTimeout(() => options.onFadeInComplete(), OPACITY_TRANSITION_TIME);
    }
  }

  stop() {
    if (!this.mounted) return;

    if (this.completeTracking) {
      this.completeTracking();
    }
  }

  toggle(on) {
    if (on) {
      this.start();
    } else {
      this.stop();
    }
  }

  renderNormally() {
    return (
      <div ref={x => (this.loadingDiv = x)} className={`loading-panel ${this.loadingPanelClassNames}`}>
        <div className={this.spinnerPanelClassNames}>
          <div className="spinner-icon" />
        </div>
        <div className={this.contentPanelClassNames}>
          {this.props.children}
        </div>
      </div>
    );
  }

  renderInline() {
    return (
      <span ref={x => (this.loadingDiv = x)} className={`loading-panel-inline ${this.loadingPanelClassNames}`}>
        <span className={this.spinnerPanelClassNames}>
          <span className="spinner-icon" />
        </span>
        <span className={this.contentPanelClassNames}>
          {this.props.children}
        </span>
      </span>
    );
  }

  renderSmall() {
    return (
      <div ref={x => (this.loadingDiv = x)} className={`loading-panel loading-panel-small ${this.loadingPanelClassNames}`}>
        <div className={this.spinnerPanelClassNames}>
          <div className="spinner-icon" />
        </div>
        <div className={this.contentPanelClassNames}>
          {this.props.children}
        </div>
      </div>
    );
  }

  renderSip() {
    return (
      <span ref={x => (this.loadingDiv = x)} className={`loading-panel-inline loading-panel-sip ${this.loadingPanelClassNames}`}>
        <span className={this.spinnerPanelClassNames}>
          <span className="spinner-icon" />
        </span>
        <span className={this.contentPanelClassNames}>
          {this.props.children}
        </span>
      </span>
    );
  }

  renderButton() {
    return (
      <div ref={x => (this.loadingDiv = x)} className={`loading-panel loading-panel-small loading-panel-button ${this.loadingPanelClassNames}`}>
        <div className={this.spinnerPanelClassNames}>
          <div className="spinner-icon" />
        </div>
        <div className={this.contentPanelClassNames}>
          {this.props.children}
        </div>
      </div>
    );
  }

  renderFullPage() {
    return (
      <div ref={x => (this.loadingDiv = x)} className={`loading-panel loading-panel-full-page ${this.loadingPanelClassNames}`}>
        <div className={this.spinnerPanelClassNames}>
          <div className="spinner-icon" />
        </div>
        <div className={this.contentPanelClassNames}>
          {this.props.children}
        </div>
      </div>
    );
  }

  render() {
    const name = _.startCase(this.props.variant).replace(' ', '') || undefined;
    const renderFunction = this[`render${name}`] || this.renderNormally;
    return renderFunction.bind(this)();
  }
}
